import { appConfig } from "@/services/config";
import type { AuthConnectivityMode } from "./connectivity";

export type SessionUser = {
  id: string;
  displayName: string;
  email?: string;
  roles: string[];
};

export type AuthSessionSource = "keycloak" | "local";

export type AuthSession = {
  accessToken: string;
  expiresAt: number;
  user: SessionUser;
  /** Where credentials were verified — Keycloak (online) or local manifest (offline). */
  source?: AuthSessionSource;
  /** Keycloak refresh token — used to renew access before expiry. */
  refreshToken?: string;
  /** Persist in localStorage (keep signed in) vs sessionStorage. */
  persist?: boolean;
};

export type LoginOptions = {
  readonly keepSignedIn?: boolean;
  /** When false, never fall back to `/demo/accounts.json` (production). */
  readonly allowDemoFallback?: boolean;
};

const STORAGE_KEY = "nbs.auth.session";
const REFRESH_SKEW_MS = 60_000;

function readRawSession(): string | null {
  return localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
}

function writeRawSession(raw: string, persist: boolean): void {
  if (persist) {
    localStorage.setItem(STORAGE_KEY, raw);
    sessionStorage.removeItem(STORAGE_KEY);
  } else {
    sessionStorage.setItem(STORAGE_KEY, raw);
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function loadSession(): AuthSession | null {
  try {
    const raw = readRawSession();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed.accessToken || !parsed.user?.id || !parsed.expiresAt) return null;
    // Access still valid (with skew) — keep it.
    if (parsed.expiresAt > Date.now() + 5_000) return parsed;
    // Expired access without refresh → drop.
    if (!parsed.refreshToken) {
      clearSession();
      return null;
    }
    // Keep expired session briefly so async refresh can revive it.
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Loads session and renews Keycloak access token when needed.
 */
export async function restoreSession(): Promise<AuthSession | null> {
  const current = loadSession();
  if (!current) return null;
  if (current.expiresAt > Date.now() + REFRESH_SKEW_MS) return current;
  if (current.refreshToken && current.source === "keycloak") {
    const renewed = await refreshKeycloakSession(current.refreshToken, Boolean(current.persist));
    if (renewed) return renewed;
  }
  clearSession();
  return null;
}

export function saveSession(session: AuthSession): void {
  writeRawSession(JSON.stringify(session), Boolean(session.persist));
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Foundation stub — real OIDC redirect/callback lands in a later phase.
 * Dev helper only: never validates permissions locally.
 */
export function createDevSession(displayName = "Utilisateur de démonstration"): AuthSession {
  return {
    accessToken: "dev-access-token", // hanfani-allow — local stub token, never a real credential
    expiresAt: Date.now() + 60 * 60 * 1000,
    user: {
      id: "dev-user",
      displayName,
      roles: [],
    },
  };
}

export type DemoLoginError =
  | "INVALID_CREDENTIALS"
  | "ACCOUNT_DISABLED"
  | "MANIFEST_UNAVAILABLE"
  | "OIDC_UNAVAILABLE"
  | "TOKEN_EXPIRED";

/** Session can be used for app navigation (access still valid, or refreshable). */
export function isSessionUsable(session: AuthSession): boolean {
  return Boolean(
    session.accessToken && ((session.expiresAt ?? 0) > Date.now() || Boolean(session.refreshToken)),
  );
}

export type DemoLoginResult =
  { ok: true; session: AuthSession } | { ok: false; error: DemoLoginError };

type DemoAccountManifestEntry = {
  email: string;
  password: string;
  displayName: string;
  role: string;
  disabled: boolean;
  accessToken: string | null;
  expiresAt: string | null;
};

/**
 * Demo-only login: matches (email, password) against the manifest generated
 * by `pnpm demo:seed` (`public/demo/accounts.json`) and, on success, adopts
 * the JWT that was minted server-side for that account during the seed run.
 */
export async function loginWithDemoAccount(
  email: string,
  password: string,
  options: LoginOptions = {},
): Promise<DemoLoginResult> {
  let manifest: { accounts: DemoAccountManifestEntry[] };
  try {
    const response = await fetch("/demo/accounts.json", { cache: "no-store" });
    if (!response.ok) return { ok: false, error: "MANIFEST_UNAVAILABLE" };
    manifest = (await response.json()) as { accounts: DemoAccountManifestEntry[] };
  } catch {
    return { ok: false, error: "MANIFEST_UNAVAILABLE" };
  }

  const account = manifest.accounts?.find(
    (a) => a.email.toLowerCase() === email.trim().toLowerCase(),
  );
  if (!account || account.password !== password) {
    return { ok: false, error: "INVALID_CREDENTIALS" };
  }
  if (account.disabled || !account.accessToken || !account.expiresAt) {
    return { ok: false, error: "ACCOUNT_DISABLED" };
  }

  const expiresAt = Date.parse(account.expiresAt);
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    // Stale seed JWT: login would toast "success" then bounce back to /login
    // because isAuthenticated requires a non-expired access token (no refresh).
    return { ok: false, error: "TOKEN_EXPIRED" };
  }

  const keepSignedIn = Boolean(options.keepSignedIn);

  return {
    ok: true,
    session: {
      accessToken: account.accessToken,
      expiresAt,
      source: "local",
      persist: keepSignedIn,
      user: {
        id: account.email,
        displayName: account.displayName,
        email: account.email,
        roles: [account.role],
      },
    },
  };
}

type KeycloakTokenResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  refresh_expires_in?: number;
  error?: string;
  error_description?: string;
};

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function rolesFromKeycloakPayload(payload: Record<string, unknown>): string[] {
  const roles: string[] = [];
  const realm = payload["realm_access"];
  if (realm && typeof realm === "object" && realm !== null) {
    const rr = (realm as { roles?: unknown }).roles;
    if (Array.isArray(rr)) {
      for (const r of rr) {
        if (
          typeof r === "string" &&
          ["ADMIN", "MANAGER", "SCIENTIST", "REVIEWER", "CONSULTANT", "PUBLIC"].includes(
            r.toUpperCase(),
          )
        ) {
          roles.push(r.toUpperCase());
        }
      }
    }
  }
  return roles.length > 0 ? roles : ["PUBLIC"];
}

function sessionFromKeycloakTokens(
  body: KeycloakTokenResponse,
  fallbackEmail: string,
  persist: boolean,
): AuthSession | null {
  if (!body.access_token) return null;
  const payload = decodeJwtPayload(body.access_token);
  if (!payload) return null;

  const expSec =
    typeof payload["exp"] === "number"
      ? payload["exp"]
      : Math.floor(Date.now() / 1000) + (body.expires_in ?? 300);
  const preferred =
    typeof payload["preferred_username"] === "string"
      ? payload["preferred_username"]
      : fallbackEmail;
  const displayName =
    typeof payload["name"] === "string" && payload["name"].length > 0 ? payload["name"] : preferred;
  const sub = typeof payload["sub"] === "string" ? payload["sub"] : preferred;

  return {
    accessToken: body.access_token,
    ...(body.refresh_token != null ? { refreshToken: body.refresh_token } : {}),
    expiresAt: expSec * 1000,
    source: "keycloak",
    persist,
    user: {
      id: sub,
      displayName,
      email: typeof payload["email"] === "string" ? payload["email"] : fallbackEmail,
      roles: rolesFromKeycloakPayload(payload),
    },
  };
}

/**
 * Renews a Keycloak access token via refresh_token grant.
 */
export async function refreshKeycloakSession(
  refreshToken: string,
  persist: boolean,
  oidcUrl: string = appConfig.oidcUrl,
): Promise<AuthSession | null> {
  const tokenUrl = `${oidcUrl.replace(/\/$/, "")}/protocol/openid-connect/token`;
  try {
    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: "nbs-web",
        refresh_token: refreshToken,
      }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as KeycloakTokenResponse;
    const session = sessionFromKeycloakTokens(body, "", persist);
    if (!session) return null;
    saveSession(session);
    return session;
  } catch {
    return null;
  }
}

/**
 * Online login against Keycloak (Direct Access Grants / password grant).
 * Requests a refresh token; with keepSignedIn also asks for offline_access.
 */
export async function loginWithKeycloakAccount(
  email: string,
  password: string,
  options: LoginOptions = {},
  oidcUrl: string = appConfig.oidcUrl,
): Promise<DemoLoginResult> {
  const keepSignedIn = Boolean(options.keepSignedIn);
  const tokenUrl = `${oidcUrl.replace(/\/$/, "")}/protocol/openid-connect/token`;
  const scope = keepSignedIn ? "openid profile offline_access" : "openid profile";

  let response: Response;
  try {
    response = await fetch(tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "password",
        client_id: "nbs-web",
        username: email.trim(),
        password,
        scope,
      }),
    });
  } catch {
    return { ok: false, error: "OIDC_UNAVAILABLE" };
  }

  let body: KeycloakTokenResponse;
  try {
    body = (await response.json()) as KeycloakTokenResponse;
  } catch {
    return { ok: false, error: "OIDC_UNAVAILABLE" };
  }

  if (!response.ok || !body.access_token) {
    const desc = (body.error_description ?? body.error ?? "").toLowerCase();
    if (desc.includes("disabled") || desc.includes("not fully set up")) {
      return { ok: false, error: "ACCOUNT_DISABLED" };
    }
    if (response.status === 401 || body.error === "invalid_grant") {
      return { ok: false, error: "INVALID_CREDENTIALS" };
    }
    return { ok: false, error: "OIDC_UNAVAILABLE" };
  }

  const session = sessionFromKeycloakTokens(body, email.trim(), keepSignedIn);
  if (!session) return { ok: false, error: "OIDC_UNAVAILABLE" };
  return { ok: true, session };
}

/**
 * Routes credentials to Keycloak when online, otherwise to the local demo manifest.
 */
export async function loginWithPassword(
  email: string,
  password: string,
  mode: AuthConnectivityMode,
  options: LoginOptions = {},
): Promise<DemoLoginResult> {
  const allowDemo = options.allowDemoFallback !== false;
  if (mode === "online") {
    const online = await loginWithKeycloakAccount(email, password, options);
    if (online.ok || online.error !== "OIDC_UNAVAILABLE") return online;
    if (!allowDemo) return online;
    return loginWithDemoAccount(email, password, options);
  }
  if (!allowDemo) return { ok: false, error: "OIDC_UNAVAILABLE" };
  return loginWithDemoAccount(email, password, options);
}

export function buildOidcAuthorizeUrl(oidcUrl: string, redirectUri: string): string {
  const url = new URL(`${oidcUrl.replace(/\/$/, "")}/protocol/openid-connect/auth`);
  url.searchParams.set("client_id", "nbs-web");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid profile");
  url.searchParams.set("redirect_uri", redirectUri);
  return url.toString();
}

/** Delay until we should refresh (1 min before expiry, min 5s). */
export function msUntilSessionRefresh(expiresAt: number): number {
  return Math.max(5_000, expiresAt - Date.now() - REFRESH_SKEW_MS);
}
